import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import axios from "axios";
import App from "./App";
import {
  AUTH_LOGOUT_EVENT,
  AUTH_TOKEN_EVENT,
  SESSION_EXPIRED_MESSAGE,
  getCurrentUser,
  getTokenExpiryMs,
  logout,
  refreshCurrentSession,
} from "./services/auth";
import { getBackendOrigin, resolveBackendUrl, shouldUseHostedBackend } from "./utils/runtimeConfig";
import 'bootstrap/dist/css/bootstrap.min.css';
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./styles/global.css";

const HOSTED_BACKEND_ORIGIN = getBackendOrigin();

if (shouldUseHostedBackend()) {
  axios.defaults.baseURL = HOSTED_BACKEND_ORIGIN;
  if (typeof window !== "undefined") {
    window.__FIRM_BACKEND_ORIGIN = HOSTED_BACKEND_ORIGIN;
  }
}
axios.defaults.withCredentials = true;

let globalLoadingRequests = 0;
let sessionExpiryTimer = null;
let forcedSessionLogout = false;

const authPaths = ["/login", "/signup", "/forgot-password", "/reset-password"];

const isAuthPath = () =>
  typeof window !== "undefined" && authPaths.includes(window.location.pathname);

const scheduleSessionExpiryCheck = () => {
  if (typeof window === "undefined") return;
  if (sessionExpiryTimer) {
    window.clearTimeout(sessionExpiryTimer);
    sessionExpiryTimer = null;
  }

  const expiresAt = getTokenExpiryMs();
  if (!expiresAt) return;

  const delay = Math.max(0, expiresAt - Date.now());
  sessionExpiryTimer = window.setTimeout(() => {
    enforceActiveSession();
  }, delay + 250);
};

const forceSessionLogout = (reason = SESSION_EXPIRED_MESSAGE) => {
  if (typeof window === "undefined") return;
  if (forcedSessionLogout) return;
  forcedSessionLogout = true;

  localStorage.removeItem("postLoginRedirect");
  logout({ reason });

  if (!isAuthPath()) {
    window.location.replace("/login");
  }
};

function enforceActiveSession() {
  const expiresAt = getTokenExpiryMs();
  if (!expiresAt) return;
  if (!expiresAt || expiresAt <= Date.now()) {
    forceSessionLogout();
    return;
  }

  scheduleSessionExpiryCheck();
}

const emitGlobalLoading = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("global-loading-change", {
      detail: { activeRequests: globalLoadingRequests },
    })
  );
};

const shouldTrackGlobalLoading = (config = {}) => {
  if (config.suppressGlobalLoader) return false;
  if (typeof window !== "undefined" && window.location.pathname === "/meal/chat-meal-planner") {
    return false;
  }
  if (config.showGlobalLoader) return true;
  const method = String(config.method || "get").toLowerCase();
  if (method === "get" && config.params && (config.params.page || config.params.offset)) {
    return true;
  }
  return ["post", "put", "patch", "delete"].includes(method);
};

const startGlobalLoading = (config = {}) => {
  if (!shouldTrackGlobalLoading(config)) return;
  config.__globalLoaderTracked = true;
  globalLoadingRequests += 1;
  emitGlobalLoading();
};

const stopGlobalLoading = (config = {}) => {
  if (!config.__globalLoaderTracked) return;
  config.__globalLoaderTracked = false;
  globalLoadingRequests = Math.max(0, globalLoadingRequests - 1);
  emitGlobalLoading();
};

// Global fallback for broken images
const FALLBACK_IMAGE = resolveBackendUrl("/assets/images/product_image_base.jpg");
if (typeof window !== "undefined") {
  const getNextImageFallback = (target) => {
    let sources = [];
    try {
      const parsed = JSON.parse(target.dataset.fallbackSrcs || "[]");
      sources = Array.isArray(parsed) ? parsed : [];
    } catch {
      sources = [];
    }

    if (!sources.length && target.dataset.fallbackSrc) {
      sources = [target.dataset.fallbackSrc];
    }

    const currentSrc = target.currentSrc || target.src;
    const next = sources.find((src) => {
      if (!src) return false;
      try {
        return new URL(src, window.location.origin).href !== currentSrc;
      } catch {
        return src !== currentSrc;
      }
    });

    if (next) {
      target.dataset.fallbackSrcs = JSON.stringify(sources.filter((src) => src !== next));
      return next;
    }

    return "";
  };

  window.addEventListener(
    "error",
    (e) => {
      const target = e.target;
      if (
        target &&
        target.tagName === "IMG" &&
        !target.dataset.fallbackApplied
      ) {
        const nextFallback = getNextImageFallback(target);
        target.dataset.fallbackApplied = "true";
        target.src = nextFallback || FALLBACK_IMAGE;
      } else if (
        target &&
        target.tagName === "IMG" &&
        target.dataset.fallbackApplied &&
        !target.dataset.placeholderFallbackApplied
      ) {
        const nextFallback = getNextImageFallback(target);
        if (nextFallback) {
          target.src = nextFallback;
          return;
        }
        target.dataset.placeholderFallbackApplied = "true";
        target.src = FALLBACK_IMAGE;
      }
    },
    true
  );
}

axios.interceptors.request.use(
  (config) => {
    config.withCredentials = config.withCredentials !== false;
    startGlobalLoading(config);
    return config;
  },
  (err) => {
    stopGlobalLoading(err.config);
    return Promise.reject(err);
  }
);

axios.interceptors.response.use(
  (res) => {
    stopGlobalLoading(res.config);
    return res;
  },
  (err) => {
    stopGlobalLoading(err.config);
    const url = err.config?.url || "";
    const status = err.response?.status;
    if (status === 401 && !url.endsWith("/login")) {
      forceSessionLogout();
    }
    return Promise.reject(err);
  }
);

if (typeof window !== "undefined" && typeof window.fetch === "function") {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const method =
      init.method ||
      (input && typeof input === "object" && "method" in input ? input.method : "get");
    const loadingConfig = { method };
    startGlobalLoading(loadingConfig);

    try {
      const nextInit = { ...init };
      if (nextInit.credentials === undefined) {
        nextInit.credentials = "include";
      }
      const requestInput =
        typeof input === "string" && input.startsWith("/")
          ? resolveBackendUrl(input)
          : input;
      const response = await nativeFetch(requestInput, nextInit);
      const url = typeof requestInput === "string" ? requestInput : requestInput?.url || "";
      if (response.status === 401 && !url.endsWith("/login")) {
        forceSessionLogout();
      }
      return response;
    } finally {
      stopGlobalLoading(loadingConfig);
    }
  };
}

if (typeof window !== "undefined") {
  window.addEventListener("focus", enforceActiveSession);
  window.addEventListener("pageshow", enforceActiveSession);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) enforceActiveSession();
  });
  window.addEventListener(AUTH_TOKEN_EVENT, () => {
    forcedSessionLogout = false;
    scheduleSessionExpiryCheck();
  });
  window.addEventListener(AUTH_LOGOUT_EVENT, (event) => {
    if (sessionExpiryTimer) {
      window.clearTimeout(sessionExpiryTimer);
      sessionExpiryTimer = null;
    }
    if (event.detail?.reason === SESSION_EXPIRED_MESSAGE && !isAuthPath()) {
      forcedSessionLogout = true;
      localStorage.removeItem("postLoginRedirect");
      window.location.replace("/login");
    }
  });
  window.addEventListener("storage", (event) => {
    if (event.key !== "auth_user") return;
    if (event.newValue) {
      forcedSessionLogout = false;
      scheduleSessionExpiryCheck();
      return;
    }
    if (!isAuthPath()) {
      localStorage.removeItem("postLoginRedirect");
      window.location.replace("/login");
    }
  });
}


async function bootstrap() {
  await refreshCurrentSession();
  const user = getCurrentUser();
  enforceActiveSession();

  // Conditionally trigger honeypot only if content protection is enabled and user is not whitelisted
  try {
    const { data: cp } = await axios.get("/api/content-protection");
    const group = user?.user_group;
    const whitelisted = Array.isArray(cp.whitelist) && group != null && cp.whitelist.includes(Number(group));
    if (cp.enabled && !whitelisted) {
      const img = new Image();
      img.src = resolveBackendUrl("/api/honeypot");
    }
  } catch {
    // ignore errors; honeypot is best-effort
  }

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}

bootstrap();
